import { Component, ChangeDetectionStrategy, inject, ChangeDetectorRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink, RouterLinkActive, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { AuthService } from '../../../core/auth/auth.service';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';

interface NavItem {
  label: string;
  icon: string;
  route: string;
  permission?: string;
  badge?: string;
}

@Component({
  selector: 'app-topbar',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterLinkActive, ButtonModule, TooltipModule, ConfirmDialogModule],
  providers: [ConfirmationService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <header class="topbar">
      <!-- Izquierda: brand + hamburger + nav -->
      <div class="topbar-left">
        <!-- Hamburger (mobile only) -->
        <button class="hamburger-btn" (click)="toggleMobileMenu()" [class.open]="mobileMenuOpen">
          <span class="hamburger-line"></span>
          <span class="hamburger-line"></span>
          <span class="hamburger-line"></span>
        </button>

        <div class="topbar-brand">
          <i class="pi pi-chart-bar" style="color:#25D366;font-size:1.3rem"></i>
          <div class="brand-titles">
            <span class="brand-main">Zentrix</span>
            <span class="brand-sub">Refactor-V10</span>
          </div>
        </div>

        <!-- Desktop Navigation -->
        <nav class="topbar-nav">
          @for (item of navItems; track item.route) {
            @if (!item.permission || hasPermission(item.permission)) {
              <a
                [routerLink]="item.route"
                routerLinkActive="active"
                class="nav-item"
              >
                <i [class]="'pi ' + item.icon + ' nav-icon'"></i>
                <span class="nav-label">{{ item.label }}</span>
                @if (item.badge) {
                  <span class="nav-badge">{{ item.badge }}</span>
                }
              </a>
            }
          }
        </nav>
      </div>

      <!-- Derecha: acciones -->
      <div class="topbar-right">
        <div class="topbar-user">
          <span class="user-role-badge">
            <i class="pi pi-key"></i> Rol <strong style="text-transform: uppercase;">{{ userRole }}</strong>
          </span>
          <button class="dark-mode-btn" (click)="toggleDarkMode()" [pTooltip]="isDarkMode ? 'Tema claro' : 'Tema oscuro'" tooltipPosition="bottom">
            <i [class]="isDarkMode ? 'pi pi-sun' : 'pi pi-moon'"></i>
          </button>
          <div class="user-avatar-container" (click)="logout()" pTooltip="Cerrar sesión" tooltipPosition="bottom">
            <div class="user-avatar-top">
              {{ getInitials(currentUser?.email) }}
            </div>
            <span class="user-name-top">{{ getUserName() }}</span>
          </div>
        </div>
      </div>
    </header>

    <!-- Mobile Navigation Overlay -->
    @if (mobileMenuOpen) {
      <div class="mobile-nav-backdrop" (click)="closeMobileMenu()"></div>
      <nav class="mobile-nav">
        @for (item of navItems; track item.route) {
          @if (!item.permission || hasPermission(item.permission)) {
            <a
              [routerLink]="item.route"
              routerLinkActive="active"
              class="mobile-nav-item"
              (click)="closeMobileMenu()"
            >
              <i [class]="'pi ' + item.icon"></i>
              <span>{{ item.label }}</span>
              @if (item.badge) {
                <span class="nav-badge">{{ item.badge }}</span>
              }
            </a>
          }
        }
      </nav>
    }

    <p-confirmDialog [style]="{width: '450px'}"></p-confirmDialog>
  `,
  styles: [`
    /* ── Topbar base ──────────────────────────────────────── */
    .topbar {
      height: 60px;
      background: #0f172a;
      border-bottom: 1px solid rgba(255,255,255,0.05);
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 0 1.25rem;
      flex-shrink: 0;
      z-index: 100;
      position: relative;
    }

    .topbar-left {
      display: flex;
      align-items: center;
      gap: 2rem;
      height: 100%;
    }

    .topbar-brand {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-weight: 700;
    }

    .brand-titles {
      display: flex;
      flex-direction: column;
      line-height: 1.1;
    }

    .brand-main { font-size: 1.1rem; color: #f8fafc; }
    .brand-sub { font-size: 0.65rem; color: #94a3b8; letter-spacing: 0.5px; }

    /* ── Desktop nav ──────────────────────────────────────── */
    .topbar-nav {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      height: 100%;
    }

    .nav-item {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem 0.8rem;
      border-radius: 6px;
      text-decoration: none;
      color: #94a3b8;
      font-size: 0.85rem;
      font-weight: 600;
      transition: all var(--transition-fast);
      cursor: pointer;
      height: 38px;

      &:hover {
        background: rgba(255,255,255,0.06);
        color: #f8fafc;
      }

      &.active {
        background: #25D366;
        color: white;
      }
    }

    .nav-icon { font-size: 0.9rem; }

    .nav-badge {
      background: rgba(255,255,255,0.2);
      border-radius: 50%;
      width: 18px;
      height: 18px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 0.65rem;
      font-weight: 700;
    }

    /* ── Topbar right ─────────────────────────────────────── */
    .topbar-right {
      display: flex;
      align-items: center;
      gap: 1rem;
    }

    .topbar-user {
      display: flex;
      align-items: center;
      gap: 1rem;
    }

    .user-role-badge {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 0.75rem;
      color: #e2e8f0;
      background: #1e293b;
      padding: 0.35rem 0.8rem;
      border-radius: 6px;
      border: 1px solid rgba(255,255,255,0.08);

      i {
        color: #3b82f6;
        font-size: 0.8rem;
      }
    }

    .dark-mode-btn {
      background: rgba(255,255,255,0.05);
      border: 1px solid rgba(255,255,255,0.1);
      color: #94a3b8;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all var(--transition-fast);
      &:hover {
        color: #f8fafc;
        background: rgba(255,255,255,0.1);
      }
    }

    .user-avatar-container {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      cursor: pointer;
      padding: 0.25rem 0.5rem;
      border-radius: 6px;
      transition: background var(--transition-fast);
      &:hover {
        background: rgba(255,255,255,0.05);
      }
    }

    .user-avatar-top {
      width: 34px; height: 34px;
      background: #059669;
      border: none;
      border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      color: white;
      font-size: 0.85rem;
      font-weight: bold;
    }

    .user-name-top {
      color: #f8fafc;
      font-size: 0.85rem;
      font-weight: 500;
    }

    /* ── Hamburger button ─────────────────────────────────── */
    .hamburger-btn {
      display: none;
      flex-direction: column;
      justify-content: center;
      gap: 5px;
      width: 36px;
      height: 36px;
      background: transparent;
      border: 1px solid rgba(255,255,255,0.1);
      border-radius: 6px;
      cursor: pointer;
      padding: 8px;
      transition: all var(--transition-fast);

      &:hover {
        background: rgba(255,255,255,0.06);
      }

      .hamburger-line {
        display: block;
        width: 100%;
        height: 2px;
        background: #94a3b8;
        border-radius: 1px;
        transition: all 250ms ease;
      }

      &.open .hamburger-line:nth-child(1) {
        transform: rotate(45deg) translate(4px, 4px);
      }
      &.open .hamburger-line:nth-child(2) {
        opacity: 0;
      }
      &.open .hamburger-line:nth-child(3) {
        transform: rotate(-45deg) translate(5px, -5px);
      }
    }

    /* ── Mobile nav overlay ───────────────────────────────── */
    .mobile-nav-backdrop {
      display: none;
    }

    .mobile-nav {
      display: none;
    }

    /* ── Responsive: mobile ≤768px ─────────────────────────── */
    @media (max-width: 768px) {
      .hamburger-btn { display: flex; }
      .topbar-nav { display: none; }
      .brand-titles { display: none; }
      .user-role-badge { display: none; }
      .user-name-top { display: none; }
      .topbar-left { gap: 0.75rem; }
      .topbar { padding: 0 0.75rem; }

      .mobile-nav-backdrop {
        display: block;
        position: fixed;
        top: 60px;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0,0,0,0.5);
        z-index: 98;
        animation: fadeIn 200ms ease;
      }

      .mobile-nav {
        display: flex;
        flex-direction: column;
        position: fixed;
        top: 60px;
        left: 0;
        right: 0;
        background: #0f172a;
        border-bottom: 2px solid rgba(37,211,102,0.3);
        z-index: 99;
        padding: 0.5rem;
        animation: slideDown 250ms ease;
        box-shadow: 0 10px 30px rgba(0,0,0,0.4);
      }

      .mobile-nav-item {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        padding: 0.75rem 1rem;
        border-radius: 8px;
        text-decoration: none;
        color: #94a3b8;
        font-size: 0.9rem;
        font-weight: 600;
        transition: all var(--transition-fast);

        &:hover {
          background: rgba(255,255,255,0.06);
          color: #f8fafc;
        }

        &.active {
          background: rgba(37,211,102,0.15);
          color: #25D366;
        }

        i {
          font-size: 1rem;
          width: 24px;
          text-align: center;
        }
      }
    }

    /* ── Responsive: tablet 769-1024px ─────────────────────── */
    @media (min-width: 769px) and (max-width: 1024px) {
      .topbar-nav { display: flex; }
      .nav-label { display: none; }
      .nav-item { padding: 0.5rem; }
      .brand-sub { display: none; }
      .user-name-top { display: none; }
    }

    /* ── Animations ───────────────────────────────────────── */
    @keyframes slideDown {
      from { opacity: 0; transform: translateY(-10px); }
      to   { opacity: 1; transform: translateY(0); }
    }

    @keyframes fadeIn {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
  `]
})
export class TopbarComponent {
  navItems: NavItem[] = [
    { label: 'Dashboard',      icon: 'pi-th-large',    route: '/dashboard' },
    { label: 'Pre-validación', icon: 'pi-shield',      route: '/pre-validacion', permission: 'NUMEROS_READ', badge: '1' },
    { label: 'Motor',          icon: 'pi-cog',         route: '/motor',          permission: 'MOTOR_VIEW' },
    { label: 'M. Maduración',  icon: 'pi-mobile',      route: '/motor-maduracion' },
    { label: 'Campañas',       icon: 'pi-send',        route: '/campanas' },
    { label: 'Monitor',        icon: 'pi-chart-line',  route: '/monitor',        permission: 'AUDIT_READ',   badge: '2' },
    { label: 'Usuarios',       icon: 'pi-users',       route: '/usuarios',       permission: 'USUARIOS_READ' },
    // { label: 'Templates',      icon: 'pi-file',        route: '/templates',      permission: 'TEMPLATE_READ' },
    { label: 'Reporte',        icon: 'pi-download',    route: '/extraccion',     permission: 'REPORTES_GENERATE' }
    // { label: 'Historial',      icon: 'pi-history',     route: '/historial-envios' }
  ];

  private authService = inject(AuthService);
  private router = inject(Router);
  private confirmationService = inject(ConfirmationService);
  private cdr = inject(ChangeDetectorRef);

  isDarkMode = true;
  mobileMenuOpen = false;

  ngOnInit() {
    this.isDarkMode = !document.documentElement.classList.contains('light-theme');
    this.authService.permissionsChanged.subscribe(() => {
      this.cdr.markForCheck();
    });
  }

  /** Cierra el menú mobile si se redimensiona a desktop */
  @HostListener('window:resize')
  onResize() {
    if (window.innerWidth > 768 && this.mobileMenuOpen) {
      this.mobileMenuOpen = false;
      this.cdr.markForCheck();
    }
  }

  toggleMobileMenu(): void {
    this.mobileMenuOpen = !this.mobileMenuOpen;
    this.cdr.markForCheck();
  }

  closeMobileMenu(): void {
    this.mobileMenuOpen = false;
    this.cdr.markForCheck();
  }

  get userRole(): string {
    if (!this.currentUser || !this.currentUser.roles) return '';
    const roleStr = this.currentUser.roles.find(r => r.startsWith('ROLE_'));
    return roleStr ? roleStr.replace('ROLE_', '') : 'N/A';
  }

  toggleDarkMode() {
    this.isDarkMode = !this.isDarkMode;
    if (this.isDarkMode) {
      document.documentElement.classList.remove('light-theme');
    } else {
      document.documentElement.classList.add('light-theme');
    }
  }

  get currentUser() { return this.authService.getCurrentUser(); }

  hasPermission(permission: string): boolean {
    return this.authService.hasPermission(permission);
  }

  logout(): void {
    this.confirmationService.confirm({
      message: '¿Estás seguro de que deseas cerrar sesión?',
      header: 'Cerrar Sesión',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, cerrar sesión',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.authService.logout();
        this.router.navigate(['/auth/login']);
      }
    });
  }

  getInitials(email: string | undefined): string {
    if (!email) return 'U';
    const parts = email.split('@');
    if (parts.length > 0) {
      return parts[0].substring(0, 2).toUpperCase();
    }
    return 'U';
  }

  getUserName(): string {
    if (this.currentUser?.nombre && this.currentUser.nombre !== 'Usuario') {
      return this.currentUser.nombre;
    }
    if (this.currentUser?.email) {
      const emailName = this.currentUser.email.split('@')[0];
      return emailName.charAt(0).toUpperCase() + emailName.slice(1);
    }
    return 'Usuario';
  }
}
